// src/components/AdminVATDashboard.js
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import io from 'socket.io-client';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './AdminVATDashboard.css';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const socket = io('https://www.panafricanbookexchange-wandererspath.com', {
    transports: ['websocket']
});

const AdminVATDashboard = () => {
    const [revenueData, setRevenueData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [isTableOpen, setIsTableOpen] = useState(false);

    // Fiscal Year Logic: Kenyan cycle starts July 1st
    const getCurrentFiscalYear = () => {
        const today = new Date();
        const year = today.getFullYear();
        return today.getMonth() >= 6 ? `${year}/${year + 1}` : `${year - 1}/${year}`;
    };

    const [selectedFiscalYear, setSelectedFiscalYear] = useState(getCurrentFiscalYear());
    const [authorFilter, setAuthorFilter] = useState('');

    useEffect(() => {
        fetchRevenue();
        socket.on('new-sale', (sale) => {
            setNotifications((prev) => [sale, ...prev].slice(0, 30));
            fetchRevenue();
        });
        return () => socket.off('new-sale');
    }, []);

    const fetchRevenue = async () => {
        try {
            const res = await axios.get(`https://panafricanbookexchange-wandererspath.com/api/auth/revenue`);
            setRevenueData(res.data);
            applyFilters(res.data, selectedFiscalYear);
        } catch (err) {
            console.error("Revenue fetch error", err);
        }
    };

    const calculateVAT = (amount) => Number(amount * 0.16);
    const calculateNetRevenue = (amount) => Number(amount * 0.84);

    const isInFiscalYear = (dateStr, fiscalYearRange) => {
        const date = new Date(dateStr);
        const [startYear] = fiscalYearRange.split('/');
        const startDate = new Date(`${startYear}-07-01`);
        const endDate = new Date(`${parseInt(startYear) + 1}-06-30`);
        return date >= startDate && date <= endDate;
    };

    const applyFilters = (dataSource = revenueData, fy = selectedFiscalYear) => {
        let data = dataSource.filter(i => isInFiscalYear(i.date, fy));
        if (authorFilter.trim() !== "") {
            data = data.filter((i) => i.author?.toLowerCase().includes(authorFilter.toLowerCase()));
        }
        setFilteredData(data);
    };

    const totals = useMemo(() => {
        return filteredData.reduce((acc, curr) => {
            const gross = Number(curr.totalRevenue) || 0;
            acc.gross += gross;
            acc.vat += calculateVAT(gross);
            acc.net += calculateNetRevenue(gross);
            return acc;
        }, { gross: 0, vat: 0, net: 0 });
    }, [filteredData]);

    // Quarterly Summary Logic
    const getQuarterlyBreakdown = () => {
        const quarters = {
            'Q1 (Jul-Sep)': { gross: 0, vat: 0 },
            'Q2 (Oct-Dec)': { gross: 0, vat: 0 },
            'Q3 (Jan-Mar)': { gross: 0, vat: 0 },
            'Q4 (Apr-Jun)': { gross: 0, vat: 0 }
        };

        filteredData.forEach(i => {
            const month = new Date(i.date).getMonth();
            let qKey;
            if (month >= 6 && month <= 8) qKey = 'Q1 (Jul-Sep)';
            else if (month >= 9 && month <= 11) qKey = 'Q2 (Oct-Dec)';
            else if (month >= 0 && month <= 2) qKey = 'Q3 (Jan-Mar)';
            else qKey = 'Q4 (Apr-Jun)';

            const gross = Number(i.totalRevenue) || 0;
            quarters[qKey].gross += gross;
            quarters[qKey].vat += calculateVAT(gross);
        });
        return quarters;
    };

    const handleFiscalChange = (e) => {
        setSelectedFiscalYear(e.target.value);
        applyFilters(revenueData, e.target.value);
    };

    const chartData = {
        labels: filteredData.map((i) => i.date),
        datasets: [
            {
                label: "Gross Revenue (Kshs)",
                data: filteredData.map((i) => Number(i.totalRevenue)),
                borderColor: '#3498db',
                backgroundColor: 'rgba(52, 152, 219, 0.2)',
                fill: true,
                tension: 0.3
            },
            {
                label: "VAT (16%)",
                data: filteredData.map((i) => calculateVAT(i.totalRevenue)),
                borderColor: '#e74c3c',
                fill: false,
                tension: 0.3
            }
        ]
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text(`Kenya VAT Report: FY ${selectedFiscalYear}`, 14, 15);
        doc.setFontSize(10);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);
        doc.text(`FY Summary: Gross: Kshs ${totals.gross.toLocaleString()} | VAT: Kshs ${totals.vat.toLocaleString()}`, 14, 28);

        const tableData = filteredData.map((i) => [
            i.date, i.reference, i.author,
            Number(i.totalRevenue).toFixed(2),
            calculateVAT(i.totalRevenue).toFixed(2),
            calculateNetRevenue(i.totalRevenue).toFixed(2)
        ]);

        autoTable(doc, {
            startY: 35,
            head: [['Date', 'Ref', 'Author', 'Gross', 'VAT', 'Net']],
            body: tableData,
            theme: 'striped'
        });
        doc.save(`KRA_VAT_Report_FY${selectedFiscalYear.replace('/', '-')}.pdf`);
    };

    return (
        <div className="admin-dashboard">
            <header className="dashboard-header">
                <h1>Fiscal Monitoring <small>(July 1 - June 30)</small></h1>
                <div className="fiscal-badge">FY {selectedFiscalYear}</div>
            </header>

            <div className="fiscal-summary-cards">
                <div className="card">
                    <h3>Annual Gross</h3>
                    <p className="amount">Kshs {totals.gross.toLocaleString()}</p>
                </div>
                <div className="card highlight">
                    <h3>Total VAT Payable (16%)</h3>
                    <p className="amount">Kshs {totals.vat.toLocaleString()}</p>
                    <small>Accrued for KRA Filing</small>
                </div>
                <div className="card">
                    <h3>Net Revenue</h3>
                    <p className="amount">Kshs {totals.net.toLocaleString()}</p>
                </div>
            </div>

            <div className="dashboard-controls">
                <div className="control-group">
                    <label>Fiscal Year Cycle:</label>
                    <select value={selectedFiscalYear} onChange={handleFiscalChange}>
                        <option value="2023/2024">FY 2023/2024</option>
                        <option value="2024/2025">FY 2024/2025</option>
                        <option value="2025/2026">FY 2025/2026</option>
<option value="2025/2026">FY 2026/2027</option>
<option value="2025/2026">FY 2027/2028</option>
<option value="2025/2026">FY 2028/2029</option>
                    </select>
                </div>

                {/* Commented out search as requested */}

                <button className="export-button" onClick={handleExportPDF}>Export KRA Report (PDF)</button>
            </div>

            <div className="chart-container" style={{ height: '400px' }}>
                <Line data={chartData} options={{ maintainAspectRatio: false, plugins: { title: { display: true, text: `Revenue Trend FY ${selectedFiscalYear}` } } }} />
            </div>

            {/* COLLAPSIBLE BAR */}
            <div 
                className={`collapsible-bar ${isTableOpen ? 'active' : ''}`} 
                onClick={() => setIsTableOpen(!isTableOpen)}
            >
                <div className="bar-content">
                    <h2>
                        Fiscal Year Breakdown & Quarterly Summary 
                        <span className="fy-indicator">FY {selectedFiscalYear}</span>
                    </h2>
                    <span className="toggle-icon">{isTableOpen ? '?' : '?'}</span>
                </div>
                {!isTableOpen && (
                    <small className="bar-hint">
                        Click to view Quarterly totals and transaction logs for {selectedFiscalYear}
                    </small>
                )}
            </div>

            {/* COLLAPSIBLE CONTENT */}
            {isTableOpen && (
                <div className="collapsed-content fade-in">
                    
                    {/* QUARTERLY SUMMARY GRID */}
                    <div className="quarterly-summary-grid">
                        {Object.entries(getQuarterlyBreakdown()).map(([qName, data]) => (
                            <div key={qName} className="q-card">
                                <h4>{qName}</h4>
                                <div className="q-row">
                                    <span>Gross:</span>
                                    <span>{data.gross.toLocaleString()}</span>
                                </div>
                                <div className="q-row vat-text">
                                    <span>VAT Due:</span>
                                    <span>{data.vat.toLocaleString()}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* REVENUE TABLE */}
                    <div className="revenue-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Reference</th>
                                    <th>Author</th>
                                    <th>Gross (Kshs)</th>
                                    <th>VAT (16%)</th>
                                    <th>Net (Kshs)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.map((i, idx) => (
                                    <tr key={idx}>
                                        <td>{i.date}</td>
                                        <td><code>{i.reference}</code></td>
                                        <td>{i.author}</td>
                                        <td>{Number(i.totalRevenue).toLocaleString()}</td>
                                        <td className="vat-cell">-{calculateVAT(i.totalRevenue).toLocaleString()}</td>
                                        <td className="net-cell">{calculateNetRevenue(i.totalRevenue).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="table-total-row">
                                    <td colSpan="3">TOTAL FY {selectedFiscalYear}</td>
                                    <td>Kshs {totals.gross.toLocaleString()}</td>
                                    <td className="vat-cell">Kshs {totals.vat.toLocaleString()}</td>
                                    <td className="net-cell">Kshs {totals.net.toLocaleString()}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminVATDashboard;